import * as React from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { EmptyState, TableSkeleton } from './feedback.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table.js';

/**
 * 一覧画面の標準テーブル。
 *
 * 並べ替えは **サーバー側**で行う前提（`sort` / `onSortChange` を受け取るだけで
 * 自前ではソートしない）。cursor ページングと組み合わせる以上、クライアントで
 * 並べ替えると「表示中のページの中だけが並ぶ」誤解を招くため。
 *
 * 読み込み中・空・エラーの 3 状態をコンポーネント側で引き受け、
 * 画面ごとに書き分けさせない。
 */

export interface DataTableColumn<T> {
  /** 列の識別子。並べ替えキーとしてサーバーへ渡す値でもある。 */
  id: string;
  header: React.ReactNode;
  /** セルの描画。 */
  cell: (row: T, index: number) => React.ReactNode;
  /** 右寄せ（数値列）。 */
  align?: 'left' | 'right' | 'center';
  /** 並べ替え可能な列か。`onSortChange` が無ければ無視される。 */
  sortable?: boolean;
  /**
   * 狭い画面で隠す列。重要度の低い補助情報に付ける。
   * **カード表示（`md` 未満）では無視される**（カードは縦積みなので幅で消す理由が無い）。
   */
  hideBelow?: 'sm' | 'md' | 'lg';
  /** 列幅のクラス（`w-32` など）。 */
  width?: string;
  className?: string;
  headClassName?: string;
}

export interface DataTableSort {
  columnId: string;
  direction: 'asc' | 'desc';
}

export interface DataTableProps<T> {
  columns: Array<DataTableColumn<T>>;
  rows: readonly T[];
  /** 行の React key。**必ず安定した ID を返すこと**（index だと再描画で状態が飛ぶ）。 */
  rowKey: (row: T, index: number) => string;
  loading?: boolean;
  /** 取得に失敗したときのメッセージ。指定すると空状態より優先して表示する。 */
  error?: React.ReactNode;
  /** 0 件のときの表示。 */
  empty?: React.ReactNode;
  emptyTitle?: React.ReactNode;
  emptyDescription?: React.ReactNode;
  emptyAction?: React.ReactNode;
  onRowClick?: (row: T) => void;
  /** 選択中の行（詳細パネルを開いている行のハイライト）。 */
  selectedKey?: string | null;
  sort?: DataTableSort | null;
  onSortChange?: (sort: DataTableSort) => void;
  /** テーブル下部（ページネーションなど）。 */
  footer?: React.ReactNode;
  /**
   * 狭い画面でもカード表示に切り替えない。
   *
   * 既定は切り替える。**列を隠すだけだと、スマートフォンでは
   * `hideBelow` を付けた列の情報に到達する手段が無くなる**（横スクロールも
   * 出ない）ため。1 列しかない表など、切り替える意味が無い場合だけ true にする。
   */
  disableCardView?: boolean;
  className?: string;
}

const HIDE_BELOW_CLASS = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
} as const;

const ALIGN_CLASS = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
} as const;

/**
 * 1 つの表に置いてよい列数の上限。
 *
 * 超えると、ノート PC の画面幅でも 1 列あたり 100px を切って本文が折り返し、
 * どの行を読んでいるのか追えなくなる。**8 を超えるなら、列を足すのではなく
 * 詳細画面かカードへ逃がす**のが正しい。強制はしない（既存の表を壊さない）が、
 * 開発中に必ず気付くよう 1 度だけ警告する。
 *
 * NODE_ENV での出し分けはしない。`packages/ui` は**ソース配布**で
 * `@types/node` を持たないため `process` を参照できず、参照すると
 * この 1 行のために型定義を足すことになる。同じ列構成につき 1 回しか
 * 出さないので、本番のコンソールが埋まることはない。
 */
export const DATA_TABLE_MAX_COLUMNS = 8;

/** 同じ表で何度も警告しないための記録（列 id の連結をキーにする）。 */
const warnedColumnSets = new Set<string>();

function warnIfTooManyColumns(ids: string[]): void {
  if (ids.length <= DATA_TABLE_MAX_COLUMNS) return;
  const key = ids.join('|');
  if (warnedColumnSets.has(key)) return;
  warnedColumnSets.add(key);
  console.warn(
    `[DataTable] 列が ${ids.length} 本あります（上限 ${DATA_TABLE_MAX_COLUMNS}）。` +
      '読み取れる幅を確保できないため、詳細画面へ逃がすか列を減らしてください: ' +
      ids.join(', '),
  );
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  error,
  empty,
  emptyTitle = 'データがありません',
  emptyDescription,
  emptyAction,
  onRowClick,
  selectedKey = null,
  sort = null,
  onSortChange,
  footer,
  disableCardView = false,
  className,
}: DataTableProps<T>) {
  warnIfTooManyColumns(columns.map((c) => c.id));

  // 初回ロードだけスケルトンにする。再取得のたびに表が消えると視線が飛ぶので、
  // 既に行がある間は表を出したまま薄くする。
  if (loading && rows.length === 0) {
    return <TableSkeleton rows={6} cols={columns.length} />;
  }

  if (error) {
    return (
      <EmptyState
        title="読み込みに失敗しました"
        description={error}
        action={emptyAction}
        className={className}
      />
    );
  }

  if (rows.length === 0) {
    if (empty) return <>{empty}</>;
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
        className={className}
      />
    );
  }

  const handleSort = (col: DataTableColumn<T>): void => {
    if (!onSortChange || !col.sortable) return;
    const next: DataTableSort =
      sort?.columnId === col.id && sort.direction === 'asc'
        ? { columnId: col.id, direction: 'desc' }
        : { columnId: col.id, direction: 'asc' };
    onSortChange(next);
  };

  /*
   * **狭い画面ではカードに切り替える。**
   *
   * 以前は `hideBelow` で列を隠すだけだったので、スマートフォンでは
   * 「状態」「最終ログイン」といった列が**どこからも見られない**状態になっていた
   * （横スクロールも出ない作りなので、隠した先に辿り着けない）。
   * 表とカードの両方を描き、CSS のブレークポイントで出し分ける。
   * 片方だけ描く形（JS で幅を測る）にしないのは、初回描画で幅が確定するまでの
   * ちらつきと、ハイドレーション時の不一致を避けるため。
   */
  const cardView = !disableCardView ? (
    <ul className={cn('space-y-2 md:hidden', loading && 'opacity-60 transition-opacity')}>
      {rows.map((row, index) => {
        const key = rowKey(row, index);
        // 先頭列を見出しに、操作列を末尾にまとめる。
        // **`id === 'actions'` も操作扱いにする。** admin / portal の操作列は
        // 見出しを `<span className="sr-only">操作</span>` にしている（表の
        // ヘッダ行を空にすると読み上げが迷子になるため）ので、header の空判定
        // だけだと「ラベルが空の詳細行」としてボタンが並び、カードが崩れる。
        const isActionColumn = (c: DataTableColumn<T>): boolean =>
          c.id === 'actions' || c.header === '' || c.header === null;
        const [primary, ...rest] = columns;
        const detail = rest.filter((c) => !isActionColumn(c));
        const actions = rest.filter(isActionColumn);
        return (
          <li
            key={key}
            className={cn(
              'rounded-lg border border-border bg-panel-elevated p-3',
              selectedKey === key && 'border-primary/60',
              onRowClick && 'cursor-pointer',
            )}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {primary ? <div className="mb-2">{primary.cell(row, index)}</div> : null}
            {detail.length > 0 ? (
              <dl className="space-y-1">
                {detail.map((col) => (
                  <div key={col.id} className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-xs text-muted-foreground">{col.header}</dt>
                    <dd className="min-w-0 text-right text-sm">{col.cell(row, index)}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
            {actions.length > 0 ? (
              <div className="mt-2 flex flex-wrap justify-end gap-1 border-t border-border pt-2">
                {actions.map((col) => (
                  <React.Fragment key={col.id}>{col.cell(row, index)}</React.Fragment>
                ))}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  ) : null;

  return (
    <div className={cn('space-y-3', className)}>
      {cardView}
      <div
        className={cn(
          !disableCardView && 'hidden md:block',
          loading && 'opacity-60 transition-opacity',
        )}
      >
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => {
                const sortable = Boolean(col.sortable && onSortChange);
                const active = sort?.columnId === col.id;
                const SortIcon = !active ? ChevronsUpDown : sort?.direction === 'asc' ? ArrowUp : ArrowDown;
                return (
                  <TableHead
                    key={col.id}
                    scope="col"
                    aria-sort={active ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={cn(
                      col.width,
                      ALIGN_CLASS[col.align ?? 'left'],
                      col.hideBelow && HIDE_BELOW_CLASS[col.hideBelow],
                      col.headClassName,
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => handleSort(col)}
                        className={cn(
                          'inline-flex items-center gap-1 rounded transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          active && 'text-foreground',
                        )}
                      >
                        {col.header}
                        <SortIcon className="size-3" aria-hidden />
                      </button>
                    ) : (
                      col.header
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => {
              const key = rowKey(row, index);
              return (
                <TableRow
                  key={key}
                  selected={selectedKey === key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  // 行クリックで遷移する表はキーボードでも辿れるようにする。
                  //
                  // **`role="button"` は付けない（R5）。** 付けると `<tr>` の
                  // 暗黙ロール `row` が上書きされ、スクリーンリーダーの表
                  // ナビゲーション（行 / 列 / セルの読み上げ）から行ごと外れる。
                  // 子の `<td>` も `cell` の文脈を失うので、「何列目の何の値か」が
                  // 読み上げられなくなる。要件（05-ui §12.1）が求める
                  // `role="row" / "cell"` を、クリック可能な表でだけ壊していた。
                  // キーボード操作は `tabIndex` + Enter / Space のハンドラで足り、
                  // フォーカス位置は table.tsx の `focus-visible:ring` が示す。
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                  className={cn(onRowClick && 'cursor-pointer')}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      className={cn(
                        ALIGN_CLASS[col.align ?? 'left'],
                        col.align === 'right' && 'tabular',
                        col.hideBelow && HIDE_BELOW_CLASS[col.hideBelow],
                        col.className,
                      )}
                    >
                      {col.cell(row, index)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {footer}
    </div>
  );
}
